import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  BookOpen,
  Users,
  Award,
  Target,
  TrendingUp,
  Brain,
  Lightbulb,
  CheckCircle2,
  ChevronRight,
  Rocket,
  Shield,
  Building2,
  Globe,
  Star,
  Briefcase,
  Stethoscope,
  FlaskConical,
  Heart,
  Activity,
  Microscope,
  Palette
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export const FacultyDevelopmentProgram = () => {
  const navigate = useNavigate();

  const pageData = {
    h1: 'Faculty Development Program',
    metaTitle: 'Faculty Development Program (FDP) | Training for Teaching Professionals',
    metaDescription: 'Join industry-aligned Faculty Development Programs designed to improve pedagogy, curriculum delivery, and academic performance.',
    metaKeywords: 'faculty development program, FDP training india, teaching pedagogy, outcome-based education, academic leadership, research skills, digital teaching, FDP for college faculty, faculty training chennai, faculty development program india',
    canonicalUrl: 'https://isde.academy/training/faculty-development-program'
  };

  const keyAreas = [
    {
      id: 'pedagogy',
      title: 'Teaching Pedagogy and Methodologies',
      icon: BookOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      gradient: 'from-blue-500 to-cyan-600',
      description: 'FDPs focus on modern, student-centric teaching approaches to enhance classroom effectiveness and student engagement through innovative methodologies.',
      topics: [
        'Active and experiential learning',
        'Problem-based and project-based learning',
        'Blended and flipped classroom models',
        'Inclusive and learner-centered education'
      ],
      benefit: 'Improved teaching delivery and enhanced student engagement through modern pedagogical approaches.'
    },
    {
      id: 'obe',
      title: 'Outcome-Based Education (OBE)',
      icon: Target,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      gradient: 'from-emerald-500 to-teal-600',
      description: 'Many Faculty Development Programs emphasize OBE to help institutions align with accreditation frameworks and ensure measurable learning outcomes.',
      topics: [
        'Course Outcomes (COs) and Program Outcomes (POs)',
        'Bloom\'s taxonomy and competency mapping',
        'Assessment planning and attainment analysis',
        'Continuous improvement strategies'
      ],
      benefit: 'Meet accreditation requirements and implement systematic outcome measurement processes.'
    },
    {
      id: 'assessment',
      title: 'Assessment and Evaluation Techniques',
      icon: CheckCircle2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-500 to-pink-600',
      description: 'Faculty are trained on designing effective assessments, implementing fair evaluation practices, and using data-driven approaches for academic decisions.',
      topics: [
        'Designing effective question papers',
        'Rubrics and continuous internal assessment',
        'Fair and transparent evaluation practices',
        'Data-driven academic decision making'
      ],
      benefit: 'Enhanced assessment quality and fair, transparent evaluation systems.'
    },
    {
      id: 'research',
      title: 'Research and Publication Skills',
      icon: Lightbulb,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      gradient: 'from-amber-500 to-orange-600',
      description: 'Research-focused FDP modules help faculty develop research capabilities, academic writing skills, and navigate the publication process effectively.',
      topics: [
        'Research methodology and design',
        'Academic writing and plagiarism prevention',
        'Journal selection and publication ethics',
        'Funded research and proposal writing'
      ],
      benefit: 'Increased research output and quality publications in peer-reviewed journals.'
    },
    {
      id: 'technology',
      title: 'Digital and Technology-Enabled Teaching',
      icon: Brain,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      gradient: 'from-rose-500 to-red-600',
      description: 'Technology integration is a key component of modern FDPs, preparing faculty to leverage digital tools for enhanced teaching and learning experiences.',
      topics: [
        'Learning Management Systems (LMS)',
        'Online teaching tools and platforms',
        'AI and digital resources in education',
        'E-content development'
      ],
      benefit: 'Effective use of technology for modern, engaging, and accessible education delivery.'
    },
    {
      id: 'leadership',
      title: 'Leadership and Academic Administration',
      icon: Users,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      gradient: 'from-indigo-500 to-blue-600',
      description: 'Faculty Development Programs also nurture leadership skills essential for academic governance, institutional planning, and quality assurance.',
      topics: [
        'Academic leadership and governance',
        'Institutional planning and quality assurance',
        'Mentoring and faculty mentoring systems',
        'Change management in education'
      ],
      benefit: 'Stronger academic leadership and effective institutional governance capabilities.'
    }
  ];

  const benefits = [
    {
      title: 'Enhanced Teaching Effectiveness',
      description: 'Faculty gain practical tools and strategies to improve classroom delivery and student engagement.',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      title: 'Improved Student Outcomes',
      description: 'Well-trained faculty contribute to better academic performance, critical thinking, and employability skills among students.',
      icon: Star,
      color: 'blue'
    },
    {
      title: 'Accreditation and Compliance Support',
      description: 'FDPs help institutions meet requirements of accreditation bodies like NBA, NAAC, NIRF, and ISO standards.',
      icon: Shield,
      color: 'purple'
    },
    {
      title: 'Professional Growth for Faculty',
      description: 'Faculty members develop confidence, leadership skills, and career advancement opportunities.',
      icon: Rocket,
      color: 'orange'
    },
    {
      title: 'Strong Institutional Reputation',
      description: 'Institutions conducting regular FDPs demonstrate commitment to quality education and continuous improvement.',
      icon: Award,
      color: 'rose'
    }
  ];

  const whoShouldAttend = [
    {
      title: 'School Teachers',
      description: 'Enhance teaching methodologies and adapt to modern pedagogical approaches',
      icon: GraduationCap,
      color: 'blue'
    },
    {
      title: 'College & University Faculty',
      description: 'Improve research capabilities, OBE implementation, and accreditation readiness',
      icon: BookOpen,
      color: 'emerald'
    },
    {
      title: 'Academic Leaders & HODs',
      description: 'Develop leadership skills for academic governance and institutional planning',
      icon: Users,
      color: 'purple'
    },
    {
      title: 'New Faculty Members',
      description: 'Build foundational teaching skills and understand academic responsibilities',
      icon: Briefcase,
      color: 'amber'
    },
    {
      title: 'Academic Coordinators',
      description: 'Strengthen administrative competencies, programme coordination, and accreditation support',
      icon: Target,
      color: 'rose'
    }
  ];

  const importance = [
    'Improve teaching quality and instructional methods',
    'Enhance student engagement and learning outcomes',
    'Support outcome-based education (OBE) implementation',
    'Meet accreditation requirements (NBA, NAAC, NIRF, ISO, etc.)',
    'Foster research culture and innovation',
    'Develop academic leadership and governance skills'
  ];

  const futureTrends = [
    { icon: Brain, text: 'Digital pedagogy and AI in education' },
    { icon: Globe, text: 'Interdisciplinary teaching' },
    { icon: Building2, text: 'Industry-academia collaboration' },
    { icon: Rocket, text: 'Lifelong learning and micro-credentials' }
  ];

  const coursesByStream = [
    {
      id: 'engineering',
      name: 'Engineering Colleges',
      icon: FlaskConical,
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-600',
      courses: [
        'Academic Leadership',
        'ICT in Teaching – Learning Practice',
        'e-Content Development',
        'Indian Knowledge System',
        'Sustainable Development Goals',
        'Research Methodology',
        'Net Zero',
        'Climate Change',
        'Incubation and Entrepreneurship Development',
        'NEP – Orientation',
        'Curriculum Development',
        'Assessment and Evaluation Techniques',
        'Effective Communication',
        'Managing Stress and Time',
        'Managing Change in Higher Education'
      ]
    },
    {
      id: 'medical',
      name: 'Medical Colleges',
      icon: Stethoscope,
      color: 'red',
      gradient: 'from-red-500 to-pink-600',
      courses: [
        'Behavioural Management',
        'Biomedical Waste Management: Theory, Regulation and Practice',
        'Competency Based Medical Education',
        'Developing Swayam Courses',
        'Entrepreneurship Skills',
        'Experimental Pharmacology',
        'Good Clinical Research Practice',
        'Good Pharmacovigilance Practices',
        'Good Prescribing Practices',
        'Health Research Methodology',
        'Integration of AYUSH and Modern Medicine',
        'Medico-Legal Cases: Management',
        'Mental Health: Managing Students',
        'Nuclear Medicine',
        'Palliative Care'
      ]
    },
    {
      id: 'dental',
      name: 'Dental Colleges',
      icon: Heart,
      color: 'emerald',
      gradient: 'from-emerald-500 to-teal-600',
      courses: [
        'Artificial Intelligence',
        'ADR Monitoring: Pharmacovigilance',
        'Antibiotics – Stewardship Training',
        'Biomedical Waste Management',
        'Good Clinical Research Practices',
        'Health Research Methodology',
        'India\'s Knowledge System Relevant to Health Care / Health Science',
        'Infection Control Practices in Health Facilities',
        'Intellectual Property Rights and Patent',
        'Public Health',
        'Rational Use of Medicines',
        'Simulation Based Training',
        'Sustainable Development Goals (Relevant to Health)'
      ]
    },
    {
      id: 'nursing',
      name: 'Nursing Colleges',
      icon: Activity,
      color: 'purple',
      gradient: 'from-purple-500 to-pink-600',
      courses: [
        'Artificial Intelligence',
        'ADR Monitoring: Pharmacovigilance',
        'Antibiotics – Stewardship Training',
        'Biomedical Waste Management',
        'Digital Health',
        'Educational Technology in Teaching and Learning Practice',
        'Health Research Methodology',
        'India\'s Knowledge System Relevant to Health Care / Health Science',
        'Infection Control Practices in Health Facilities',
        'Intellectual Property Rights and Patent',
        'Machine Learning',
        'Public Health',
        'Rational Use of Medicines',
        'Simulation Based Training',
        'Sustainable Development Goals (Relevant to Health)'
      ]
    },
    {
      id: 'physiotherapy',
      name: 'Physiotherapy Colleges',
      icon: Microscope,
      color: 'orange',
      gradient: 'from-orange-500 to-amber-600',
      courses: [
        'Research and Publication Ethics',
        'Artificial Intelligence',
        'Machine Learning',
        'Health System Accreditation',
        'Evidence Based Medicines',
        'Social and Psychological Aspects of Physical Medicine',
        'Yoga for Health',
        'Pain Management through Physiotherapy',
        'Research Grant Writing',
        'Collaborative Healthcare'
      ]
    },
    {
      id: 'allied-health',
      name: 'Allied Health Professions',
      icon: Users,
      color: 'indigo',
      gradient: 'from-indigo-500 to-blue-600',
      courses: [
        'Research and Publication Ethics',
        'Artificial Intelligence',
        'Machine Learning',
        'Health System Accreditation',
        'Evidence Based Medicines',
        'Social and Psychological Aspects of Physical Medicine / Nuclear Medicines',
        'Yoga for Health',
        'Research Grant Writing',
        'Managing Human Resources in Health System',
        'Collaborative Healthcare'
      ]
    },
    {
      id: 'arts-science',
      name: 'Arts and Science Colleges',
      icon: Palette,
      color: 'rose',
      gradient: 'from-rose-500 to-red-600',
      courses: [
        'Academic Leadership',
        'Artificial Intelligence',
        'Bioinformatics',
        'Cyber Security',
        'e-Content Development',
        'ICT in Teaching – Learning Practice',
        'Indian Knowledge System',
        'IPR and Patent',
        'Machine Learning',
        'Managing Stress and Time',
        'NEP: Orientation',
        'Research Methodology: Experimental Research (Science)',
        'Research Methodology: Operational Research (Arts)',
        'Sustainable Goals',
        'Writing Funding Proposals',
        'Yoga and Wellness'
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Faculty Development Program",
    "description": "Industry-aligned Faculty Development Programs designed to improve pedagogy, curriculum delivery, and academic performance for teaching professionals in India.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "ISDE Academy",
      "url": "https://isde.academy"
    },
    "courseCode": "FDP",
    "educationalLevel": "Professional Development",
    "teaches": [
      "Teaching Pedagogy",
      "Outcome-Based Education",
      "Research Skills",
      "Digital Teaching",
      "Academic Leadership"
    ],
    "availableLanguage": "English",
    "geographicArea": {
      "@type": "Country",
      "name": "India"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a Faculty Development Program (FDP)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A Faculty Development Program (FDP) is a systematic training initiative aimed at improving the knowledge, skills, attitudes, and teaching capabilities of faculty members in schools, colleges, universities, and professional institutions. FDPs focus on pedagogical skills, research capabilities, assessment methods, academic leadership, and technology in education."
        }
      },
      {
        "@type": "Question",
        "name": "Who should attend a Faculty Development Program?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A Faculty Development Program is ideal for school teachers, college and university faculty members, newly appointed lecturers and professors, senior faculty and academic leaders, heads of departments (HODs), and academic coordinators and administrators."
        }
      },
      {
        "@type": "Question",
        "name": "What are the key topics covered in an FDP?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key topics in a Faculty Development Program include teaching pedagogy and methodologies, outcome-based education (OBE), assessment and evaluation techniques, research and publication skills, digital and technology-enabled teaching, and leadership and academic administration."
        }
      },
      {
        "@type": "Question",
        "name": "How does FDP help with accreditation like NBA and NAAC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "FDPs help institutions meet requirements of accreditation bodies like NBA, NAAC, NIRF, and ISO standards by training faculty in outcome-based education (OBE), documentation standards, assessment planning, and continuous improvement processes."
        }
      },
      {
        "@type": "Question",
        "name": "How is the effectiveness of a Faculty Development Program measured?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The success of a Faculty Development Program is evaluated through participant feedback, improvement in teaching practices, student performance indicators, research output and publications, and accreditation outcomes. Continuous evaluation helps refine future FDP initiatives."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageData.metaTitle}</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.metaKeywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageData.metaTitle} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta property="og:site_name" content="ISDE Academy" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.metaTitle} />
        <meta name="twitter:description" content={pageData.metaDescription} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-700 text-white py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <GraduationCap className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <BookOpen className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">Training for Teaching Professionals</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {pageData.h1}
              </h1>

              <p className="text-xl text-blue-100 mb-4 leading-relaxed max-w-3xl mx-auto">
                In today's rapidly evolving education landscape, the role of educators extends far beyond classroom teaching. Faculty members are expected to be mentors, researchers, curriculum designers, and leaders who can adapt to new technologies, pedagogical methods, and accreditation requirements. A well-structured Faculty Development Program (FDP) plays a crucial role in enhancing teaching effectiveness, academic leadership, and institutional quality.
              </p>
              <p className="text-lg text-blue-200 mb-8 leading-relaxed max-w-3xl mx-auto">
                Faculty Development Programs are designed to strengthen the professional competencies of teachers, improve student learning outcomes, and align institutions with national and international academic standards.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-indigo-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-indigo-600 font-semibold px-8 py-6 text-lg"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">What Is a Faculty Development Program?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              A Faculty Development Program is a systematic training initiative aimed at improving the knowledge, skills, attitudes, and teaching capabilities of faculty members in schools, colleges, universities, and professional institutions. FDPs focus on enhancing pedagogical skills, research capabilities, assessment methods, academic leadership, and use of technology in education.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              These programs support continuous professional development and ensure educators remain relevant, effective, and learner-centric.
            </p>
          </div>
        </section>

        {/* Importance Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Importance of Faculty Development Program</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl">
              The quality of education largely depends on the competence and commitment of faculty members. A structured Faculty Development Program helps institutions:
            </p>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
              {importance.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-lg">{item}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-700 mt-8 text-lg font-medium max-w-4xl">
              Institutions that invest in FDPs demonstrate a strong commitment to academic excellence and continuous improvement.
            </p>
          </div>
        </section>

        {/* Key Areas Covered */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Key Areas Covered in Faculty Development Program</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {keyAreas.map((area, index) => {
                const IconComponent = area.icon;
                return (
                  <motion.article
                    key={area.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="h-full"
                  >
                    <Card className="h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-2 group flex flex-col">
                      <div className={`bg-gradient-to-br ${area.gradient} p-6 text-white flex flex-col h-80`}>
                        <div className={`${area.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform flex-shrink-0`}>
                          <IconComponent className={`h-8 w-8 ${area.color}`} />
                        </div>
                        <h4 className="text-2xl font-bold mb-3 flex-shrink-0">
                          {area.title}
                        </h4>
                        <p className="text-white/90 text-sm leading-relaxed flex-1 overflow-hidden">
                          {area.description}
                        </p>
                      </div>
                      <CardContent className="p-6 bg-white flex-1 flex flex-col">
                        <h5 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wide">Key Topics:</h5>
                        <ul className="space-y-2 mb-4 flex-1">
                          {area.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className={`h-4 w-4 ${area.color} flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-600 text-sm">{topic}</span>
                            </li>
                          ))}
                        </ul>
                        <div className={`${area.bgColor} p-3 rounded-lg mt-auto`}>
                          <p className={`text-sm font-medium ${area.color}`}>{area.benefit}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Courses by Stream Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">FDP Courses by Academic Stream</h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We offer specialized Faculty Development Programs tailored to the unique needs of different academic disciplines and institutional types.
              </p>
            </div>

            <Tabs defaultValue="engineering" className="w-full">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 h-auto bg-transparent mb-8">
                {coursesByStream.map((stream) => {
                  const IconComponent = stream.icon;
                  const colors = {
                    blue: 'data-[state=active]:bg-blue-600 data-[state=active]:text-white hover:bg-blue-50',
                    red: 'data-[state=active]:bg-red-600 data-[state=active]:text-white hover:bg-red-50',
                    emerald: 'data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-50',
                    purple: 'data-[state=active]:bg-purple-600 data-[state=active]:text-white hover:bg-purple-50',
                    orange: 'data-[state=active]:bg-orange-600 data-[state=active]:text-white hover:bg-orange-50',
                    indigo: 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white hover:bg-indigo-50',
                    rose: 'data-[state=active]:bg-rose-600 data-[state=active]:text-white hover:bg-rose-50'
                  };

                  return (
                    <TabsTrigger
                      key={stream.id}
                      value={stream.id}
                      className={`flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg transition-all ${colors[stream.color]}`}
                    >
                      <IconComponent className="h-6 w-6" />
                      <span className="text-xs font-semibold text-center leading-tight">{stream.name}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {coursesByStream.map((stream) => {
                const IconComponent = stream.icon;
                const colorClasses = {
                  blue: { gradient: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
                  red: { gradient: 'from-red-500 to-pink-600', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
                  emerald: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
                  purple: { gradient: 'from-purple-500 to-pink-600', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
                  orange: { gradient: 'from-orange-500 to-amber-600', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
                  indigo: { gradient: 'from-indigo-500 to-blue-600', bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
                  rose: { gradient: 'from-rose-500 to-red-600', bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' }
                };
                const colors = colorClasses[stream.color];

                return (
                  <TabsContent key={stream.id} value={stream.id} className="mt-0">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Card className={`border-2 ${colors.border} overflow-hidden`}>
                        <div className={`bg-gradient-to-r ${colors.gradient} p-6 text-white`}>
                          <div className="flex items-center gap-4">
                            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                              <IconComponent className="h-8 w-8" />
                            </div>
                            <div>
                              <h4 className="text-2xl font-bold">Faculty Development Programme for {stream.name}</h4>
                              <p className="text-white/90 mt-1">{stream.courses.length} specialized courses available</p>
                            </div>
                          </div>
                        </div>
                        <CardContent className="p-6">
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {stream.courses.map((course, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className={`flex items-start gap-3 p-3 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-md transition-shadow`}
                              >
                                <div className={`${colors.text} flex-shrink-0 mt-0.5`}>
                                  <CheckCircle2 className="h-5 w-5" />
                                </div>
                                <span className="text-gray-700 text-sm font-medium leading-tight">{course}</span>
                              </motion.div>
                            ))}
                          </div>

                          <div className={`mt-6 p-4 rounded-lg ${colors.bg} border ${colors.border}`}>
                            <p className={`text-sm font-semibold ${colors.text} mb-2`}>
                              ✓ Customized for {stream.name}
                            </p>
                            <p className="text-gray-600 text-sm">
                              All courses can be tailored to your institution's specific requirements and delivered on-campus or online.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </TabsContent>
                );
              })}
            </Tabs>

            <div className="mt-12 text-center">
              <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-200">
                <CardContent className="p-8">
                  <h5 className="text-2xl font-bold text-gray-900 mb-3">Need a Custom Program?</h5>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    We can design bespoke Faculty Development Programs combining courses from multiple streams to meet your institution's specific needs.
                  </p>
                  <Button
                    onClick={() => navigate('/contact')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-6 text-lg"
                  >
                    Request Custom Program
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Benefits of Faculty Development Program</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                const colors = {
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
                  rose: { bg: 'bg-rose-50', text: 'text-rose-600' }
                };
                const color = colors[benefit.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6">
                        <div className={`${color.bg} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-2 text-gray-900">{benefit.title}</h5>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who Should Attend */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-4 text-center text-gray-900">Who Should Attend Faculty Development Program?</h4>
            <p className="text-lg text-gray-600 text-center mb-10 max-w-3xl mx-auto">
              A Faculty Development Program is ideal for educators and academic professionals at all levels and institutions.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whoShouldAttend.map((audience, index) => {
                const IconComponent = audience.icon;
                const colors = {
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
                  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                  rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' }
                };
                const color = colors[audience.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`h-full bg-white border-2 ${color.border} hover:shadow-xl transition-all hover:-translate-y-2`}>
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-8 w-8 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-3 text-gray-900">{audience.title}</h5>
                        <p className="text-gray-600 text-sm">{audience.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Measuring Effectiveness */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Measuring Effectiveness of Faculty Development Program</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-4xl">
              The success of a Faculty Development Program can be evaluated through:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
              {[
                { icon: Users, text: 'Feedback from participants' },
                { icon: TrendingUp, text: 'Improvement in teaching practices' },
                { icon: GraduationCap, text: 'Student performance indicators' },
                { icon: Lightbulb, text: 'Research output and publications' },
                { icon: Shield, text: 'Accreditation outcomes' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-lg p-4"
                  >
                    <IconComponent className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-gray-600 mt-6 text-lg max-w-4xl">
              Continuous evaluation helps refine and improve future FDP initiatives.
            </p>
          </div>
        </section>

        {/* Future Trends */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Future Trends in Faculty Development Program</h4>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl">
              As education evolves, FDPs will increasingly focus on:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {futureTrends.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-indigo-200 hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6 flex items-center gap-3">
                        <IconComponent className="h-8 w-8 text-indigo-600 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">{item.text}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-gray-700 mt-8 text-lg italic max-w-3xl">
              Institutions that adapt to these trends will remain competitive and future-ready.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">Frequently Asked Questions About Faculty Development Programs</h2>
              <div className="space-y-4">
                {faqStructuredData.mainEntity.map((faq, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                    <h3 className="font-semibold text-gray-900 mb-2">{faq.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Teaching Practice?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Join leading educators who have enhanced their teaching effectiveness with our comprehensive Faculty Development Programs
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-indigo-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Your Faculty
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-indigo-600 font-semibold px-8 py-6 text-lg"
                >
                  View Training Calendar
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FacultyDevelopmentProgram;
